import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input, Modal, Select, Textarea } from '@koyomi/ui';
import { api, ApiError } from '../../lib/api.ts';
import { useAuthStore } from '../../app/auth-store.ts';
import { fmtDateTime } from '../../lib/time.ts';

const REASONS = ['PRICE', 'NOT_USING', 'MISSING_FEATURE', 'SWITCHED_TOOL', 'CLOSING_BUSINESS', 'OTHER'] as const;
interface Preview { effectiveAt: string; immediate: boolean; retentionDays: number; openEntries: number }

/**
 * Cancelamento pelo cliente em duas etapas: (1) o que acontece e quando, (2) motivo + nome do negócio para confirmar.
 * Vale no fim do período já pago; até lá o aviso na página permite desfazer.
 */
export function CancelSubscriptionModal({ businessName, tz, onClose }: { businessName: string; tz: string; onClose: () => void }) {
  const { t, i18n } = useTranslation(['config', 'common']);
  const qc = useQueryClient();
  const businessId = useAuthStore((s) => s.businessId);
  const preview = useQuery({ queryKey: [businessId, 'cancel-preview'], queryFn: () => api<Preview>('/business/subscription/cancel-preview'), staleTime: 0 });
  const [step, setStep] = useState<1 | 2>(1);
  const [reason, setReason] = useState<(typeof REASONS)[number] | ''>('');
  const [feedback, setFeedback] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const p = preview.data;
  const date = p ? fmtDateTime(p.effectiveAt, tz, i18n.language, { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
  const nameOk = confirm.trim().toLowerCase() === businessName.trim().toLowerCase();

  const submit = async () => {
    if (!reason || !nameOk) return;
    setBusy(true); setError(null);
    try {
      await api('/business/subscription/cancel', { body: { reason, feedback: feedback.trim() || undefined, confirmName: confirm.trim() } });
      await Promise.all([qc.invalidateQueries({ queryKey: [businessId, 'subscription'] }), qc.invalidateQueries({ queryKey: [businessId, 'business'] })]);
      if (p?.immediate) window.location.assign('/');
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common:errors.INTERNAL'));
    } finally { setBusy(false); }
  };

  const footer = step === 1
    ? <><Button onClick={onClose}>{t('config:plan.cancel.keep')}</Button><Button variant="danger" disabled={!p} onClick={() => setStep(2)}>{t('config:plan.cancel.continue')}</Button></>
    : <><Button onClick={() => setStep(1)}>{t('common:actions.back')}</Button><Button variant="danger" loading={busy} disabled={!reason || !nameOk} onClick={submit}>{p?.immediate ? t('config:plan.cancel.confirmNow') : t('config:plan.cancel.confirm', { date })}</Button></>;

  return (
    <Modal open onClose={onClose} title={t('config:plan.cancel.title')} size="md" footer={footer}>
      {preview.isLoading && <span className="k-spinner" />}
      {preview.isError && <p className="k-field__error" role="alert">{preview.error instanceof ApiError ? preview.error.message : t('common:errors.INTERNAL')}</p>}
      {p && step === 1 && (
        <div className="k-stack" style={{ gap: 12 }}>
          <p style={{ margin: 0, fontWeight: 600 }}>{p.immediate ? t('config:plan.cancel.whenNow') : t('config:plan.cancel.when', { date })}</p>
          <ul className="plan__list" style={{ margin: 0 }}>
            {!p.immediate && <li>{t('config:plan.cancel.untilThen')}</li>}
            <li>{t('config:plan.cancel.noMoreCharges')}</li>
            {p.openEntries > 0 && <li>{t('config:plan.cancel.openEntries', { count: p.openEntries })}</li>}
            <li>{t('config:plan.cancel.afterwards')}</li>
            <li>{t('config:plan.cancel.retention', { days: p.retentionDays })}</li>
            {!p.immediate && <li>{t('config:plan.cancel.undo')}</li>}
          </ul>
        </div>
      )}
      {p && step === 2 && (
        <div className="k-stack" style={{ gap: 12 }}>
          <Select label={t('config:plan.cancel.reason')} value={reason} onChange={(e) => setReason(e.target.value as (typeof REASONS)[number])}>
            <option value="" disabled>{t('config:plan.cancel.pick')}</option>
            {REASONS.map((r) => <option key={r} value={r}>{t(`config:plan.cancel.reasons.${r}`)}</option>)}
          </Select>
          <Textarea label={t('config:plan.cancel.feedback')} rows={3} maxLength={1000} value={feedback} onChange={(e) => setFeedback(e.target.value)} />
          <Input label={t('config:plan.cancel.typeName', { name: businessName })} autoComplete="off" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          {error && <div className="k-field__error" role="alert">{error}</div>}
        </div>
      )}
    </Modal>
  );
}
